import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SecurityProvider } from "@/components/SecurityProvider";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import Index from "./pages/Index";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy-load non-index routes to reduce initial JS bundle size
const NotFound = lazy(() => import("./pages/NotFound"));
const CRRFlash = lazy(() => import("./pages/CRRFlash"));
const CRRZone1 = lazy(() => import("./pages/CRRZone1"));
const CRRZone2 = lazy(() => import("./pages/CRRZone2"));
const CRRZone3 = lazy(() => import("./pages/CRRZone3"));
const CRRZone4 = lazy(() => import("./pages/CRRZone4"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const MaritimeCareers = lazy(() => import("./pages/MaritimeCareers"));
const PermisAcademy = lazy(() => import("./pages/PermisAcademy"));
const AdminFlashcards = lazy(() => import("./pages/AdminFlashcards"));
const Install = lazy(() => import("./pages/Install"));
const ScubaDiving = lazy(() => import("./pages/ScubaDiving"));
const DyslexicMarinerProfiles = lazy(() => import("./pages/DyslexicMarinerProfiles"));
const Islands = lazy(() => import("./pages/Islands"));

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SecurityProvider>
          <AccessibilityProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Suspense fallback={<div className="min-h-screen bg-background" />}>
                  <Routes>
                    {/* All public routes — no gates */}
                    <Route path="/" element={<Index />} />
                    <Route path="/crr-flash" element={<CRRFlash />} />
                    <Route path="/crr-zone1" element={<CRRZone1 />} />
                    <Route path="/crr-zone2" element={<CRRZone2 />} />
                    <Route path="/crr-zone3" element={<CRRZone3 />} />
                    <Route path="/crr-zone4" element={<CRRZone4 />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/maritime-careers" element={<MaritimeCareers />} />
                    <Route path="/permis-academy" element={<PermisAcademy />} />
                    <Route path="/admin-flashcards" element={<AdminFlashcards />} />
                    <Route path="/install" element={<Install />} />
                    <Route path="/scuba-diving" element={<ScubaDiving />} />
                    <Route path="/dt-profiles" element={<DyslexicMarinerProfiles />} />
                    <Route path="/islands" element={<Islands />} />
                    <Route path="/guadeloupe" element={<Islands />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AccessibilityProvider>
        </SecurityProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
